import React, { useEffect, useState } from "react";
import { connect, Provider } from "react-redux";
import Modal from "../../../general/modal";
import Icon from "../../../general/icon";
import {
  isHideModalDataSelector,
  isShowModalDataSelector,
  modalDataSelector,
  modalNameSelector,
} from "../../../../../store/selector/general";
import { general } from "../../../../../store/action";
import VisitMedicationPrescriptionTable from "./dataList/medication/visitMedicationPrescriptionTable";
import VisitParaclinicalPrescriptionTable from "./dataList/paraclinical/visitParaclinicalPrescriptionTable";
import { useAddPrescription } from "../../../../../hook/request/visit/prescription";
import { useMedicalHistory } from "../../../../../hook/request/visit/visits";
import { TPrescriptionFields } from "../../../../../types/prescription";
import BoxLoader from "../../../general/boxLoader";
import { baseFormat } from "../../../../../utils/date";
import { useWindowSize } from "../../../../../hook/useWindowSize";
import VisitMedicationPrescriptionCards from "./dataList/medication/visitMedicationPrescriptionCards";
import VisitParaclinicalPrescriptionCards from "./dataList/paraclinical/visitParaclinicalPrescriptionCards";
import {
  localStorageGet,
  localStorageRemove,
} from "../../../../../utils/localStorage";
import { toast } from "react-hot-toast";
import { TGIdNameData } from "../../../../../types/general";
import { ISSUER_TYPE } from "../../../../../constants/constant/enum";
import SelectField from "../../../general/form/selectField";
import NumberField from "../../../general/form/numberField";
import { NumericFormat } from "react-number-format";
import { useSakhadInquiryPrescription } from "../../../../../hook/request/providerSakhad";
import { TVisitFields } from "../../../../../types/visit";
import prescription from "../../../../pages/visits/visitDetail/prescription/prescription";
import { useGetTaminAuthStatus } from "../../../../../hook/request/providerTamin";

const medicationDataListTitles = [
  "برند",
  "واحد",
  "مقدار مصرف",
  "عدد",
  "دوز",
  "نام دارو",
  "شکل دارو",
];

const paraclinicalDataListTitles = ["تعداد", "تاریخ", "نوع خدمت", "عنوان"];

type AddVisitPrescriptionModalProps = {
  modalData: any;
  modalName: string;
  isShowModalData: boolean;
  handleHideModal: any;
  handleResetModal: any;
  handleShowModal: any;
};

type ModalInfoProps = {
  id: string;
  visitId: string;
  insurance?: TGIdNameData;
  patientData?: any;
  // medicationItemsFieldValue?: any;
  // paraclinicalItemsFieldValue?: any;
  prescriptionFieldValue?: TPrescriptionFields;
  medicalHistoryFieldValue?: any;
  visitValues?: TVisitFields;
  draftDescription?: string;
  handlePrescriptionModalSubmit: any;
  willDoItMyselfCounter?: React.MutableRefObject<number>;
  sakhadRejected?: boolean; // @AMIR
};

const AddVisitPrescriptionModal = ({
  modalData,
  modalName,
  isShowModalData,
  handleHideModal,
  handleResetModal,
  handleShowModal,
}: AddVisitPrescriptionModalProps) => {
  const [width] = useWindowSize();
  const [showState, setShowState] = useState(false);
  const [modalInfo, setModalInfo] = useState<ModalInfoProps>({
    id: "",
    visitId: "",
    patientData: "",
    handlePrescriptionModalSubmit: null,
  });
  const [medicalHistoryFieldValue, setMedicalHistoryFieldValue] = useState([]);
  const [draftDescription, setDraftDescription] = useState();
  const [prescriptionData, setPrescriptionData] =
    useState<TPrescriptionFields>();
  const [ihioMedicalRepetition, setIhioMedicalRepetition] = useState(0);
  const [
    isEsataInquiryPrescriptionRejected,
    setIsEsataInquiryPrescriptionRejected,
  ] = useState(false);

  // NEW: State for loading status during the inquiry
  const [isEsataInquiryLoading, setIsEsataInquiryLoading] = useState(false);

  // const [isTaminTokenValid, setIsTaminTokenValid] = useState(false);

  // NEW: State for potential error messages from the inquiry
  const [esataInquiryError, setEsataInquiryError] = useState<string>();

  // services
  const addPrescriptionRequest = useAddPrescription({
    onSuccess(result: any) {
      handleSetShowModal();
      if (!result?.insurerStatus?.errorMessage) {
        // console.log("TAMIN_useAddPres 02");
        handleShowModal("endVisitModal", {
          willDoItMyselfCounter: modalInfo.willDoItMyselfCounter,
        });
      }
    },
  });
  const addMedicalHistoryRequest = useMedicalHistory({
    onSuccess() {},
  });

  const getTaminAuthStatus = useGetTaminAuthStatus();
  const getSakhadInquiryPrescriptionRequest = useSakhadInquiryPrescription();
  const transformMedicationItem = (item: any, modalData: any) => {
    // Ensure that item.frequency and item.dosage are full objects
    // If they are coming as just IDs, you need to create the object around them.
    // Based on your initial console.log, frequency and dosage were just IDs (149, 2447)
    // but your transform implies they are already objects {id, name, description}.
    // I'll assume for this robust transform that you'll either pass full objects
    // or just the IDs which then need to be wrapped.

    // Re-checking your initial console.log for medicationItems:
    // "frequency": 149,
    // "dosage": 2447,
    // This means your `item.frequency` and `item.dosage` in the `prescriptionData.medicationItems`
    // array are currently simple numbers, not objects.
    // So, we need to wrap them in objects with an 'id' property.

    // Also, your `MedicationPrescriptionItemData` has a `provider` and `prescription` field.
    // These need to be populated as `IdNameData` objects.

    // console.log("SAKHAD_001", item);
    return {
      draft: false,

      provider: {
        id: modalData?.visitValues?.provider?.id || 0, // Provide default 0 if null
        name: modalData?.visitValues?.provider?.name || "",
      },

      item: {
        id: item.item.id || 0,
        molecule: item.item.molecule || null,
        category: item.item.category || null, // Ensure category is IdNameData: { id: ..., name: ... }
        shape: item.item.shape || null, // Ensure shape is IdNameData: { id: ..., name: ... }
        dose: item.item.dose || null,
        doseForm: item.item.doseForm || null,
        generic: item.item.generic || null,
        erx: item.item.erx || null,
        sakhadSupportStatus: item.item.sakhadSupportStatus || 0,
        // Add other MedicationItemData fields if they are consistently available and needed.
        // Example: name, faName, description, irc, brand, brandName, ihioSupport, ihioActive, taminSupport, taminActive, sakhadActive, sakhadSupport, sakhadShape
        // If not provided, they will be null on backend due to JsonIgnoreProperties.
        // You should provide any non-null defaults if required by backend logic.
      },

      // 5. Frequency needs to be a MaxaDetailData object with at least an 'id'
      frequency: {
        id: item.frequency?.id || item.frequency || 0, // Handles if already object or just ID
        // If you have name/description for frequency, include them too:
        // name: item.frequency?.name || null,
        // description: item.frequency?.description || null,
      },

      // 6. Dosage needs to be a MedicationDosageData object with at least an 'id'
      dosage: {
        id: item.dosage?.id || item.dosage || 0, // Handles if already object or just ID
        // If you have name/faName/amount for dosage, include them too:
        // name: item.dosage?.name || null,
        // faName: item.dosage?.faName || null,
        // amount: item.dosage?.amount || 0,
        // shape: item.dosage?.shape || null, // IdNameData object
      },

      itemCount: parseFloat(item.itemCount), // Ensure it's a float

      // 7. bulkId: long.
      bulkId: item.bulkId || 0,

      // 8. Explicitly set optional fields to null if not available,
      // to match backend structure and avoid sending unexpected fields.
      expireDate: null,
      activeDate: null,
      futureDates: null,
      drugDescription: item?.drugDescription,
      patientInstruction: item?.drugDescription,
      ihioCheck: null,
      // The 'brand' field you had previously in the JSON body is *not* in MedicationPrescriptionItemData.
      // If you need to send 'brand' information, it should be nested inside `item` (MedicationItemData).
      // Or if `brand` has a specific purpose that's not `MedicationItemData.brand`,
      // then the backend `MedicationPrescriptionItemData` class needs that field.
      // For now, based on your classes, `MedicationPrescriptionItemData` does not have a `brand` field directly.
      // So, we omit it here if it's not meant to be within `item`.
      // brand: item.brand, // Omit this if MedicationPrescriptionItemData doesn't have a 'brand' field.
    };
  };
  const transformParaclinicalItem = (item: any, modalData: any) => {
    return {
      draft: false, // Default value, adjust if your UI handles drafts
      provider: {
        id: modalData?.visitValues?.provider?.id || 0,
        name: modalData?.visitValues?.provider?.name || "",
      },

      service: {
        id: item.service.id || 0,
        type: item.service.type || null,
        name: item.service.name || null,
        enName: item.service.enName || null,
        description: item.service.description || null,
        code: item.service.code || null,
        loinc: item.service.loinc || null,
        rvu3: item.service.rvu3 || null,
        taminServiceTypeId: item.service.taminServiceTypeId || 0,
        taminPrescribeTypeId: item.service.taminPrescribeTypeId || 0,
        taminParaclinicGroupId: item.service.taminParaclinicGroupId || 0,
        support: item.service.support || false,
        active: item.service.active || false,
        sakhadTypeCode: item.service.sakhadTypeCode || null,
      },
      bodySite: null,
      bodySiteDescription: null,
      itemCount: parseInt(item.itemCount, 10) || 0,
      // activeDate: convertUnixToISO(item.activeDate),

      expireDate: null,
      emergency: item.emergency || false,
      note: item.note || null,
      patientInstruction: null,
      ihioCheck: null,
      referred: null,
      physioIllness: null,
      physioPlans: [],
      complaints: [],
      icd10s: [],
    };
  };
  // useEffect(() => {
  //   if (isShowModalData && modalName === "addVisitPrescriptionModal") {
  //     if (modalData?.insurance?.id == ISSUER_TYPE.ESATA.value) {
  //       const prescriptionBody = {
  //         provider: {
  //           id: modalData?.visitValues?.provider?.id,
  //           name: modalData?.visitValues?.provider?.name,
  //         },
  //         patient: {
  //           id: modalData?.visitValues?.patient?.id,
  //           name: modalData?.visitValues?.patient?.name,
  //         },
  //         insurer: {
  //           id: modalData?.insurance?.id,
  //           name: modalData?.insurance?.name,
  //         },
  //         medicationItems: prescriptionData?.medicationItems?.map((item) =>
  //           transformMedicationItem(item, modalData),
  //         ),
  //         paraclinicalItems: prescriptionData?.paraclinicalItems?.map((item) =>
  //           transformParaclinicalItem(item, modalData),
  //         ),
  //       };
  //       setIsEsataInquiryLoading(true);
  //       setEsataInquiryError(""); // Clear previous errors
  //       // console.log("PARA_05", prescriptionBody);
  //       // Inside the first useEffect, find getSakhadInquiryPrescriptionRequest.mutate
  //       getSakhadInquiryPrescriptionRequest.mutate(prescriptionBody, {
  //         onSuccess: (response: any) => {
  //           // 1. Check if the status is 200 based on your API structure (usually response.data.status)
  //           const status = response?.data?.status;

  //           if (status === 200) {
  //             setIsEsataInquiryPrescriptionRejected(
  //               response?.data?.result?.reject,
  //             );
  //             setEsataInquiryError(""); // Clear any previous errors
  //           } else {
  //             // 2. If not 200, treat as error
  //             setIsEsataInquiryPrescriptionRejected(true);
  //             setEsataInquiryError(
  //               response?.data?.msg || "خطا در استعلام از سامانه ساخد",
  //             );
  //           }

  //           setIsEsataInquiryLoading(false);
  //         },
  //         onError: (error: any) => {
  //           setEsataInquiryError("اختلال در برقراری ارتباط با سرور");
  //           setIsEsataInquiryLoading(false);
  //           setIsEsataInquiryPrescriptionRejected(true);
  //         },
  //       });
  //       // console.log("PARA_07", modalData);
  //     }
  //     setShowState(true);
  //     setModalInfo(modalData);
  //     setPrescriptionData(modalData?.prescriptionFieldValue);
  //     setMedicalHistoryFieldValue(modalData?.medicalHistoryFieldValue);
  //     setDraftDescription(modalData?.draftDescription);
  //     handleResetModal();
  //   }
  // }, [modalData, modalName]);

  // useEffect(() => {
  //   getSakhadInquiryPrescriptionRequest?.data?.data?.result &&
  //     setIsEsataInquiryPrescriptionRejected(
  //       addPrescriptionRequest?.data?.data?.result?.reject,
  //     );
  //   // console.log(
  //   //   "PARA_08",
  //   //   getSakhadInquiryPrescriptionRequest?.data?.data?.result?.reject
  //   // );
  // }, [getSakhadInquiryPrescriptionRequest.data]);

  useEffect(() => {
    if (
      isShowModalData &&
      modalName === "addVisitPrescriptionModal" &&
      modalData
    ) {
      // 1. Update states for the UI
      setShowState(true);
      setModalInfo(modalData);
      setPrescriptionData(modalData?.prescriptionFieldValue);
      setMedicalHistoryFieldValue(modalData?.medicalHistoryFieldValue);
      setDraftDescription(modalData?.draftDescription);
      handleResetModal();

      // 2. Use modalData directly for the condition (don't use modalInfo here)
      if (modalData?.insurance?.id === ISSUER_TYPE.ESATA.value) {
        // Use modalData.prescriptionFieldValue directly (don't use prescriptionData here)
        const currentPrescription = modalData?.prescriptionFieldValue;

        const prescriptionBody = {
          provider: {
            id: modalData?.visitValues?.provider?.id,
            name: modalData?.visitValues?.provider?.name,
          },
          patient: {
            id: modalData?.visitValues?.patient?.id,
            name: modalData?.visitValues?.patient?.name,
          },
          insurer: {
            id: modalData?.insurance?.id, // Use modalData
            name: modalData?.insurance?.name, // Use modalData
          },
          medicationItems: currentPrescription?.medicationItems?.map(
            (item: any) => transformMedicationItem(item, modalData),
          ),
          paraclinicalItems: currentPrescription?.paraclinicalItems?.map(
            (item: any) => transformParaclinicalItem(item, modalData),
          ),
        };

        setIsEsataInquiryLoading(true);
        setEsataInquiryError("");

        getSakhadInquiryPrescriptionRequest.mutate(prescriptionBody, {
          onSuccess: (response: any) => {
            const status = response?.data?.status;
            if (status === 200) {
              setIsEsataInquiryPrescriptionRejected(
                response?.data?.result?.reject,
              );
              setEsataInquiryError("");
            } else {
              setIsEsataInquiryPrescriptionRejected(true);
              setEsataInquiryError(
                response?.data?.msg || "خطا در استعلام از سامانه ساخد",
              );
            }
            setIsEsataInquiryLoading(false);
          },
          onError: (error: any) => {
            setEsataInquiryError("اختلال در برقراری ارتباط با سرور");
            setIsEsataInquiryLoading(false);
            setIsEsataInquiryPrescriptionRejected(true);
          },
        });
      }
    }
  }, [modalData, modalName, isShowModalData]); // Added isShowModalData as dependency

  useEffect(() => {
    addPrescriptionRequest?.data?.data?.result &&
      setPrescriptionData(addPrescriptionRequest?.data?.data.result);
  }, [addPrescriptionRequest.data]);

  useEffect(() => {
    if (prescriptionData && prescriptionData.id) {
      const { id, insurerStatus } = prescriptionData;
      modalInfo.handlePrescriptionModalSubmit(id, insurerStatus);
    }
  }, [prescriptionData]);

  const isCheckingTaminAuth = getTaminAuthStatus.isPending;
  // useEffect(() => {
  //   if (getTaminAuthStatus?.data?.data?.result) {
  //     const { expire, expireTime } = getTaminAuthStatus.data.data.result;
  //     setIsTaminTokenValid(!expire);
  //   }
  // }, [getTaminAuthStatus?.data?.data?.result]);

  const checkForTaminAuthorization = async () => {
    // Check if insurance ID matches TAMIN
    if (modalInfo?.insurance?.id !== ISSUER_TYPE.TAMIN.value) {
      return true; // No need to check authorization for non-TAMIN issuers
    }

    try {
      const response = await getTaminAuthStatus.mutateAsync();
      const { expire } = response?.data?.result ?? {};
      return !expire;
    } catch (error) {
      return false;
    }

    // const taminAuthorization = localStorageGet("TAMIN_ACCESS_TOKEN");
    // console.log(taminAuthorization);
    // if (!taminAuthorization) {
    //   return false; // No authorization token found
    // }

    // const expireTime =
    //   taminAuthorization.date_time + taminAuthorization.expires_in * 1000;
    // if (expireTime > Date.now()) {
    //   return true; // Token is valid
    // }

    // // Token is expired, remove it
    // localStorageRemove("TAMIN_ACCESS_TOKEN");
    // return false;
  };

  const endVisit = async () => {
    if (await checkForTaminAuthorization()) {
      const transformedMedicationArray =
        prescriptionData?.medicationItems?.flatMap(
          ({
            checkCode,
            item,
            frequency,
            dosage,
            itemCount,
            activeDate,
            futureDates,
            drugDescription,
            bulkId,
            bulkItems,
          }) => {
            if (bulkId === 0) {
              const itemId = item?.id;
              if (itemId === undefined || itemCount === undefined) return [];
              return [
                {
                  item: {
                    id: itemId,
                    erx: item?.erx,
                    shape: item?.shape,
                    dose: item?.dose,
                    irc: item?.irc,
                    category: item?.category,
                  },
                  frequency: { id: frequency },
                  dosage: { id: dosage },
                  ihioCheck: checkCode,
                  activeDate: baseFormat(activeDate),
                  bulkId: 0,
                  itemCount,
                  futureDates,
                  drugDescription,
                },
              ];
            } else {
              return (
                bulkItems?.flatMap((bulkItem) => {
                  const bulkItemId = bulkItem?.item?.id;
                  const bulkItemCount = bulkItem?.itemCount;
                  if (bulkItemId === undefined || bulkItemCount === undefined)
                    return [];

                  return {
                    item: { id: bulkItemId },
                    frequency: { id: frequency },
                    dosage: { id: dosage },
                    ihioCheck: bulkItem.checkCode,
                    activeDate: baseFormat(activeDate),
                    itemCount: bulkItemCount,
                    drugDescription: bulkItem.drugDescription,
                    bulkId,
                  };
                }) ?? []
              );
            }
          },
        );

      const transformedParaclinicalArray =
        prescriptionData?.paraclinicalItems?.map(
          ({
            checkCode,
            service,
            itemCount,
            emergency,
            note,
            illness,
            plan,
            complaints,
            icd10s,
            activeDate,
          }) => {
            const { id: serviceId } = service || {},
              physioPlans = plan?.map((item: any) => {
                return { id: item?.value };
              }),
              complaintsArray = complaints?.map((item: any) => {
                return { id: item?.value };
              }),
              icd10sArray = icd10s?.map((item: any) => {
                return { id: item?.value };
              });
            return {
              service: {
                id: serviceId,
              },
              itemCount,
              emergency,
              physioIllness: {
                id: illness,
              },
              physioPlans: physioPlans,
              complaints: complaintsArray,
              icd10s: icd10sArray,
              note,
              ihioCheck: checkCode,
              activeDate: baseFormat(activeDate),
            };
          },
        );

      const prescriptionBody = {
        id: prescriptionData?.id,
        visit: { id: modalInfo?.visitId },
        patient: {
          id: modalInfo.patientData?.id,
          name: modalInfo.patientData?.name,
        }, // @AMIR
        sakhadPrescriptionInputApiDeleteModelList:
          prescriptionData?.sakhadPrescriptionInputApiDeleteModelList,
        // insurer: { id: modalInfo?.insurance?.id }, // @AMIR
        medicationItems: transformedMedicationArray,
        paraclinicalItems: transformedParaclinicalArray,
        draftDescription: modalInfo?.draftDescription,
        referralFeedbackId: prescriptionData?.referralFeedbackId,
        referralFeedbackDescription:
          prescriptionData?.referralFeedbackDescription,
        ihioMedicalRepetition: ihioMedicalRepetition, // تکرار نسخه بیمه سلامت
        sakhadComplaintCode: prescriptionData?.sakhadComplaintCode, //@AMIR
      };
      const medicalHistoryBody = {
        id: modalInfo?.visitId,
        chiefComplaint: modalInfo?.medicalHistoryFieldValue?.chiefComplaint,
        clinicalFindings: modalInfo?.medicalHistoryFieldValue?.clinicalFindings,
        examinationNote: modalInfo?.medicalHistoryFieldValue?.examinationNote,
        labResultNote: modalInfo?.medicalHistoryFieldValue?.labResultNote,
        visitGoalNote: modalInfo?.medicalHistoryFieldValue?.visitGoalNote,

        // ...medicalHistoryFieldValue,
      };
      // console.log("VISIT_10", medicalHistoryBody);
      addMedicalHistoryRequest.mutate(medicalHistoryBody);
      addPrescriptionRequest.mutate(prescriptionBody);
      // console.log("SAKHAD_0015", prescriptionBody);
    } else {
      toast(
        <div>
          <div className="d-flex align-items-center">
            <span className="d-flex align-items-center font-25 ms-2">
              <Icon name="tamin-logo" />
            </span>
            <span>
              پزشک گرامی ورود شما به سامانه تأمین اجتماعی برای تجویز نسخه الزامی
              است.
            </span>
          </div>
          <div
            className="d-flex align-items-center justify-content-center justify-content-md-end m-md-0 px-2"
            onClick={() => {
              handleSetShowModal();
              handleShowModal("taminLoginModal");
            }}
          >
            <span className="btn btn-dark rounded rounded-3 font-10 d-flex align-items-center fw-bold px-3">
              ورود به سامانه
            </span>
          </div>
        </div>,
        {
          position: "top-center",
          duration: 10000,
          style: {
            borderRadius: "10px",
            background: "#f8d7da", // Semi-transparent background
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 14px 28px, rgba(0, 0, 0, 0.22) 0px 10px 10px",
            overflow: "hidden",
            backdropFilter: "blur(10px)", // Blur effect behind the component
            WebkitBackdropFilter: "blur(10px)", // For compatibility with WebKit browsers
            width: "1000px",
          },
        },
      );
    }
  };

  const handleSetShowModal = () => {
    setShowState(false);
    handleHideModal();
  };

  return (
    <Modal
      setShow={handleSetShowModal}
      showState={showState}
      noCloseBtn={true}
      className="w-1000 mw-100"
      bodyClassName="px-3 px-lg-5 pos-rel"
      fullscreen={width <= 900 ? true : "false"}
    >
      {(addPrescriptionRequest?.isPending ||
        addMedicalHistoryRequest.isPending) && <BoxLoader type="cover" />}
      <div className="form-box w-1000 mw-100 mx-auto py-3">
        <div className="d-flex mb-4 fs-3 px-2">
          <div className=" px-2 flex-fill align-content-center">
            <h4 className="d-flex align-items-center title-dot font-16 font-weight-bold">
              خلاصه اطلاعات ویزیت
            </h4>
            {modalInfo?.patientData && (
              <span className="d-inline-flex fw-semibold text-primary mx-4 font-12">
                {modalInfo?.patientData?.name}
              </span>
            )}
          </div>
          <span
            className="text-red-hover align-content-center mt-2"
            onClick={() => handleSetShowModal()}
          >
            <Icon name="close-square" />
          </span>
        </div>
        <h4 className="d-flex font-14 mb-2 pb-2">
          <div className="d-flex align-items-center align-content-center border-bottom ms-auto ps-5">
            <span className="d-flex font-22">
              <Icon name="pills" />
            </span>
            <span className="me-2">تجویز دارویی</span>
            <span className="bg-gray3 text-gray rounded-3 fw-semibold font-12 me-2 px-2 py-1">
              {prescriptionData?.medicationItems?.length} قلم
            </span>
          </div>
          {modalInfo?.insurance?.id === ISSUER_TYPE.IHIO.value && (
            <>
              <NumericFormat
                value={String(ihioMedicalRepetition)}
                onChange={(event) =>
                  setIhioMedicalRepetition(Number(event?.target?.value))
                }
                name={"ihioMedicalRepetition"}
                className={"w-110 form-control form-control-sm "}
                placeholder={"تعداد تکرار نسخه"}
              />
            </>
          )}
          <div className="d-flex align-items-center align-items-stretch justify-content-center justify-content-md-start mb-3 m-md-0">
            <span
              onClick={() =>
                handleShowModal("favoritePrescriptionModal", {
                  prescriptionData: prescriptionData?.medicationItems,
                  draftMedication: true,
                })
              }
              className={`btn btn-gray rounded d-flex align-items-center ${
                width > 900 ? "px-4 mx-2" : "px-2"
              }`}
            >
              <span
                className={`d-inline-flex font-20 ${width > 900 && "ms-2"}`}
              >
                <Icon name="add-square" />
              </span>
              {width > 900 && "افزودن به نسخ پر استفاده"}
            </span>
          </div>
        </h4>
        <section
          className="table-cont table-responsive custom-scrollbar border rounded mb-5"
          style={{ maxHeight: "25vh" }}
        >
          {width > 900 ? (
            <VisitMedicationPrescriptionTable
              dataList={prescriptionData?.medicationItems}
              dataListTitles={medicationDataListTitles}
              isRequesting={false}
            />
          ) : (
            <VisitMedicationPrescriptionCards
              dataList={prescriptionData?.medicationItems}
              dataListTitles={medicationDataListTitles}
              isRequesting={false}
            />
          )}
        </section>
        <h4 className="d-flex font-14 mb-2 pb-2">
          <div className="d-flex align-items-center align-content-center border-bottom ms-auto ps-5">
            <span className="d-flex font-22">
              <Icon name="test" />
            </span>
            <span className="me-2">تجویز پاراکلینیک</span>
            <span className="bg-gray3 text-gray rounded-3 fw-semibold font-12 me-2 px-2 py-1">
              {prescriptionData?.paraclinicalItems?.length} خدمت
            </span>
          </div>
          <div className="d-flex align-items-center align-items-stretch justify-content-center justify-content-md-start mb-3 m-md-0">
            <span
              onClick={() =>
                handleShowModal("favoritePrescriptionModal", {
                  prescriptionData: prescriptionData?.paraclinicalItems,
                  draftMedication: false,
                })
              }
              className={`btn btn-gray rounded d-flex align-items-center ${
                width > 900 ? "px-4 mx-2" : "px-2"
              }`}
            >
              <span
                className={`d-inline-flex font-20 ${width > 900 && "ms-2"}`}
              >
                <Icon name="add-square" />
              </span>
              {width > 900 && "افزودن به نسخ پر استفاده"}
            </span>
          </div>
        </h4>
        <section
          className="table-cont table-responsive custom-scrollbar border rounded overflow-y-auto"
          style={{ maxHeight: "25vh" }}
        >
          {width > 900 ? (
            <VisitParaclinicalPrescriptionTable
              dataList={prescriptionData?.paraclinicalItems}
              dataListTitles={paraclinicalDataListTitles}
              isRequesting={false}
            />
          ) : (
            <VisitParaclinicalPrescriptionCards
              dataList={prescriptionData?.paraclinicalItems}
              dataListTitles={paraclinicalDataListTitles}
              isRequesting={false}
            />
          )}
        </section>
        <div className="pt-4 flex-fill align-content-center">
          {modalInfo?.insurance?.id === ISSUER_TYPE.ESATA.value && (
            <span className="d-inline-flex mx-4 font-14">
              {isEsataInquiryLoading ? (
                <>
                  <i className="fa fa-spinner fa-spin me-2"></i>
                  <Icon name={"history"} />
                  <span className="text-gray2">در حال استعلام اولیه...</span>
                </>
              ) : esataInquiryError ? (
                <span className="text-red">
                  <Icon name={"close-square"} /> {esataInquiryError}
                </span>
              ) : !isEsataInquiryLoading &&
                !isEsataInquiryPrescriptionRejected ? (
                <span className="text-green2">
                  <Icon name={"check-square"} /> استعلام اولیه نسخه با موفقیت
                  انجام شد.
                </span>
              ) : (
                isEsataInquiryPrescriptionRejected && (
                  <span className="text-red">
                    <Icon name={"close-square"} /> در استعلام اولیه نسخه تداخلی
                    یافت شد.
                  </span>
                )
              )}
            </span>
          )}
        </div>

        <div className="col-12 d-flex justify-content-end mt-4">
          <div className="d-flex w-100 align-items-stretch form-group ">
            <button
              type="submit"
              className="btn btn-primary rounded d-flex w-100 justify-content-center align-items-center"
              onClick={endVisit}
              disabled={
                isCheckingTaminAuth ||
                (modalInfo?.insurance?.id == ISSUER_TYPE.ESATA.value &&
                  isEsataInquiryPrescriptionRejected)
              }
            >
              {isCheckingTaminAuth ? "در حال بررسی..." : "ثبت و تایید نسخه"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: any) => ({
  modalData: modalDataSelector(state),
  modalName: modalNameSelector(state),
  isShowModalData: isShowModalDataSelector(state),
  isHideModalData: isHideModalDataSelector(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  handleHideModal: () => dispatch(general.handleHideModal()),
  handleResetModal: () => dispatch(general.handleResetModal()),
  handleShowModal: (data: any, body: any) =>
    dispatch(general.handleShowModal(data, body)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddVisitPrescriptionModal);
